import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged, 
  User as FirebaseUser,
  UserCredential
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { openaiService } from './openaiService';

// Define the API key response interface
interface ApiKeyResponse {
  key?: string;
  apiKey?: string;
  [key: string]: any;
}

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Enable logging for debugging - set to false in production
const DEBUG = process.env.NODE_ENV !== 'production';

// Logger function
const log = (message: string, data?: any) => {
  if (DEBUG) {
    console.log(`[FirebaseService] ${message}`);
    // Only log data if it's not sensitive
    if (data && !isSensitiveData(data)) {
      console.log(data);
    }
  }
};

// Helper to check if data might contain sensitive information
const isSensitiveData = (data: any): boolean => {
  // Check if the data might contain API keys or other sensitive info
  if (typeof data === 'string') {
    return data.includes('sk-') || data.includes('apiKey') || data.includes('key');
  }
  
  if (typeof data === 'object' && data !== null) {
    return Object.keys(data).some(key => 
      key.toLowerCase().includes('key') || 
      key.toLowerCase().includes('token') || 
      key.toLowerCase().includes('secret') ||
      key.toLowerCase().includes('password')
    );
  }
  
  return false;
};

// Initialize Firebase
let app: any;
let auth: any;
let functions: any;
let getApiKeyFunction: any;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  functions = getFunctions(app);
  getApiKeyFunction = httpsCallable(functions, 'getApiKey');
  log('Firebase initialized successfully');
} catch (error) {
  log('Error initializing Firebase:', error);
}

// Local storage keys
const API_KEYS_KEY = 'green_tokens_api_keys';
const FIREBASE_USER_KEY = 'green_tokens_firebase_user';

/**
 * Firebase service for authentication and API key management
 * This service works alongside the existing Spotify authentication
 */
export class FirebaseService {
  private apiKeys: string[] = [];
  private firebaseUser: FirebaseUser | null = null;
  private isAuthenticated: boolean = false;
  private cachedApiKey: string | null = null;
  private lastApiKeyFetch: number = 0;
  private readonly API_KEY_CACHE_DURATION = 1000 * 60 * 60; // 1 hour in milliseconds

  constructor() {
    this.loadFromStorage();
    
    // Set up auth state listener
    if (auth) {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          log('Firebase auth state changed: User is signed in');
          this.handleAuthStateChange(user);
        } else {
          log('Firebase auth state changed: User is signed out');
          this.clearUserData();
        }
      });
    }
  }

  // Load data from local storage
  private loadFromStorage(): void {
    const apiKeysJson = localStorage.getItem(API_KEYS_KEY);
    if (apiKeysJson) {
      try {
        this.apiKeys = JSON.parse(apiKeysJson);
        log('Loaded API keys from storage', { count: this.apiKeys.length });
      } catch (error) {
        log('Error parsing API keys from localStorage:', error);
        this.apiKeys = [];
      }
    }

    const firebaseUserJson = localStorage.getItem(FIREBASE_USER_KEY);
    if (firebaseUserJson) {
      try {
        const userData = JSON.parse(firebaseUserJson);
        this.isAuthenticated = true;
        log('Loaded Firebase user data from storage');
      } catch (error) {
        log('Error parsing Firebase user data from localStorage:', error);
      }
    }
  }

  // Save API keys to local storage
  private saveApiKeysToStorage(apiKeys: string[]): void {
    localStorage.setItem(API_KEYS_KEY, JSON.stringify(apiKeys));
    this.apiKeys = apiKeys;
    log('Saved API keys to storage', { count: apiKeys.length });
  }

  // Save Firebase user data to storage
  private saveFirebaseUserToStorage(user: FirebaseUser): void {
    // Store minimal user data
    const userData = {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified
    };
    localStorage.setItem(FIREBASE_USER_KEY, JSON.stringify(userData));
    this.firebaseUser = user;
    this.isAuthenticated = true;
    log('Saved Firebase user data to storage');
  }

  // Handle Firebase auth state change
  private async handleAuthStateChange(user: FirebaseUser): Promise<void> {
    if (user) {
      this.saveFirebaseUserToStorage(user);
      
      // Fetch API keys
      await this.fetchApiKeys();
    }
  }

  // Clear user data on logout
  private clearUserData(): void {
    localStorage.removeItem(FIREBASE_USER_KEY);
    localStorage.removeItem(API_KEYS_KEY);
    this.firebaseUser = null;
    this.apiKeys = [];
    this.isAuthenticated = false;
    
    // Clear the API key cache
    this.cachedApiKey = null;
    this.lastApiKeyFetch = 0;
    
    // Clear the OpenAI client cache to force a new API key fetch on next use
    openaiService.clearCache();
    
    log('Cleared Firebase user data and API keys');
  }

  // Check if the user is logged in to Firebase
  public isLoggedIn(): boolean {
    return this.isAuthenticated;
  }

  // Get the first API key (for LLM calls)
  public getApiKey(): string | null {
    return this.apiKeys.length > 0 ? this.apiKeys[0] : null;
  }

  // Get all API keys
  public getAllApiKeys(): string[] {
    return [...this.apiKeys];
  }

  // Login with email and password
  public async login(email: string, password: string): Promise<boolean> {
    try {
      log('Attempting Firebase login');
      if (!auth) {
        log('Firebase auth not initialized');
        return false;
      }
      
      const userCredential: UserCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      log('Firebase login successful');
      this.saveFirebaseUserToStorage(user);
      
      // Fetch API keys
      await this.fetchApiKeys();
      
      return true;
    } catch (error) {
      log('Firebase login error:', error);
      return false;
    }
  }

  // Fetch API keys from Firebase function
  public async fetchApiKeys(): Promise<string[]> {
    try {
      log('Fetching API keys from Firebase function');
      if (!getApiKeyFunction) {
        log('Firebase functions not initialized');
        return [];
      }
      
      // Clear the OpenAI client cache before fetching new API keys
      // This ensures that the next OpenAI API call will use the new key
      openaiService.clearCache();
      
      // Use the enhanced getUserApiKey function
      const apiKey = await this.getUserApiKey();
      
      if (apiKey) {
        const apiKeys = [apiKey];
        log('API key fetched successfully');
        this.saveApiKeysToStorage(apiKeys);
        return apiKeys;
      } else {
        // Fallback to the original implementation
        const result = await getApiKeyFunction();
        const apiKeys = result.data as string[];
        
        if (Array.isArray(apiKeys) && apiKeys.length > 0) {
          log('API keys fetched successfully (fallback method)', { count: apiKeys.length });
          this.saveApiKeysToStorage(apiKeys);
          return apiKeys;
        } else {
          log('No API keys returned from Firebase function');
          return [];
        }
      }
    } catch (error) {
      log('Error fetching API keys:', error);
      return [];
    }
  }
  
  // Get user's API key with enhanced error handling and caching
  public async getUserApiKey(): Promise<string | null> {
    // Check if we have a cached API key that's not expired
    const now = Date.now();
    if (this.cachedApiKey && (now - this.lastApiKeyFetch) < this.API_KEY_CACHE_DURATION) {
      log('getUserApiKey - Using cached API key');
      return this.cachedApiKey;
    }
    
    if (!functions) return null;
    log('getUserApiKey - Attempting to call getApiKey Cloud Function...');
    try {
      const getApiKeyFunction = httpsCallable(functions, 'getApiKey');
      // Explicitly type the expected response data structure
      const result = await getApiKeyFunction() as { data: string | ApiKeyResponse | unknown }; 
      const data = result.data;
      // Don't log the actual API key data in production
      log('getUserApiKey - Received response from Cloud Function');

      let apiKey: string | null = null;
      
      // Check if the returned data is the key string directly
      if (typeof data === 'string' && data.startsWith('sk-')) {
        log("getUserApiKey - Received key directly as string.");
        apiKey = data;
      } 
      // Check if it's an object matching our expected structure
      else if (typeof data === 'object' && data !== null) {
        const responseData = data as ApiKeyResponse; // Type assertion
        if (typeof responseData.key === 'string' && responseData.key.startsWith('sk-')) {
          log("getUserApiKey - Received key in 'key' property.");
          apiKey = responseData.key;
        }
        else if (typeof responseData.apiKey === 'string' && responseData.apiKey.startsWith('sk-')) {
          log("getUserApiKey - Received key in 'apiKey' property.");
          apiKey = responseData.apiKey;
        }
      }
      
      if (apiKey) {
        // Cache the API key
        this.cachedApiKey = apiKey;
        this.lastApiKeyFetch = now;
        return apiKey;
      }
      
      // If we reach here, the format is unexpected or key is missing/invalid
      log('getUserApiKey - Unexpected data format received from getApiKey Cloud Function:', data);
      return null;
    } catch (error) {
      log('getUserApiKey - Error calling getApiKey Cloud Function:', error);
      return null;
    }
  }

  // Logout from Firebase
  public async logout(): Promise<void> {
    try {
      log('Logging out from Firebase');
      if (!auth) {
        log('Firebase auth not initialized');
        return;
      }
      
      await signOut(auth);
      this.clearUserData();
      log('Firebase logout successful');
    } catch (error) {
      log('Firebase logout error:', error);
    }
  }

  // Redirect to signup page
  public redirectToSignup(): void {
    log('Redirecting to Green Tokens signup page');
    window.location.href = 'https://dashboard.green-tokens.com/signup';
  }
}

// Create a singleton instance
export const firebaseService = new FirebaseService();
